import React, { useEffect, useState } from 'react';
import { Modal, Form, Select } from 'antd';
import { Warning, CaretDown, SpinnerGap, MagnifyingGlass } from '@phosphor-icons/react';
import { ButtonCommon, ErrorMessage } from 'components/common';
import { DeactivateModalProps } from 'types';
import { useDebouncedCallback } from 'use-debounce';

const DeactivateModal: React.FC<DeactivateModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  activeRecords,
  recordToDeactivate,
  isUpdating = false,
  title = 'Hủy kích hoạt',
  entityName = 'đơn vị',
  onSearch,
  isSearchLoading = false,
}) => {
  const [form] = Form.useForm();
  const [selectedRecord, setSelectedRecord] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const debouncedSearch = useDebouncedCallback((value: string) => {
    if (onSearch) {
      onSearch(value);
    }
  }, 300);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedRecord('');
      setSearchValue('');
      if (onSearch) {
        onSearch('');
      }
    }
  }, [isOpen, form, onSearch, recordToDeactivate]);

  const availableRecords = React.useMemo(() => {
    if (!activeRecords || !recordToDeactivate) {
      return [];
    }
    const filtered = activeRecords.filter((record) => {
      return record.key !== recordToDeactivate.key;
    });
    return filtered;
  }, [activeRecords, recordToDeactivate]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onConfirm(values.replacementRecord);
    });
  };

  const getDescription = () => {
    if (title.includes('Xóa')) {
      return `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} ${recordToDeactivate?.name} đang được sử dụng tại các mục khác. Vui lòng chọn ${entityName} mới để thay thế trước khi xóa:`;
    }
    return `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} ${recordToDeactivate?.name} đang được sử dụng tại các mục khác. Vui lòng chọn ${entityName} mới để thay thế trước khi hủy kích hoạt:`;
  };

  return (
    <Modal
      open={isOpen}
      // onCancel={onCancel}
      footer={null}
      width={500}
      height={262}
      centered
      className='deactivate-modal'
      closeIcon={null}
    >
      <div className='bg-white flex flex-col gap-[16px] rounded-t-[8px] p-[24px] '>
        <div className='flex gap-3'>
          <Warning size={60} weight='fill' color='var(--sematicWarningColor)' />
          <div className='text-[var(--opacityTextColor)] flex flex-col gap-1'>
            <span className='text-[20px] font-bold leading-[24px]'>{title}</span>
            <p className='text-[14px] leading-[20px]'>{getDescription()}</p>
          </div>
        </div>

        <Form form={form} layout='vertical' requiredMark={false}>
          <Form.Item
            name='replacementRecord'
            className='px-[24px]'
            label={
              <span className='relative text-[14px] leading-[20px] font-medium'>
                Chọn {entityName}{' '}
                <span className='absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
              </span>
            }
            rules={[
              { required: true, message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải chọn giá trị!' /> },
            ]}
          >
            <Select
              placeholder={`Tìm kiếm ${entityName}...`}
              className='w-full'
              onChange={(value) => setSelectedRecord(value)}
              suffixIcon={<CaretDown size={16} color='var(--textBlack)' />}
              showSearch
              onSearch={handleSearch}
              searchValue={searchValue}
              filterOption={false}
              notFoundContent={
                isSearchLoading ? (
                  <div className='flex justify-start items-center'>
                    <SpinnerGap size={18} weight='bold' className='text-[var(--primaryColor500)] animate-spin' />
                    <span className='ml-2'>Đang tìm kiếm...</span>
                  </div>
                ) : availableRecords.length === 0 ? (
                  <div className='flex justify-start items-center'>
                    <MagnifyingGlass size={18} weight='bold' className='text-[var(--textBlack56)]' />
                    <span className='ml-2'>Không tìm thấy {entityName} phù hợp</span>
                  </div>
                ) : null
              }
            >
              {availableRecords.map((record) => (
                <Select.Option key={String(record?.key)} value={record.key}>
                  <div className='flex flex-col'>
                    <span className='font-medium'>{String(record?.name || '')}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <div className='flex justify-end gap-3'>
          <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel}>
            Hủy
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='md'
            radius='rounded'
            onClick={handleSubmit}
            disabled={!selectedRecord}
            isDisabled={!selectedRecord}
            loading={isUpdating}
          >
            Thay thế
          </ButtonCommon>
        </div>
      </div>
    </Modal>
  );
};

export default DeactivateModal;
